import { useState } from 'react'
import { Monitor, Ban, BarChart3, PenLine, Users } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import Layout from '../../components/Layout'
import ComposeView from '../../components/admin/crm/ComposeView'
import ContactsView from '../../components/admin/crm/ContactsView'
import ResultsView from '../../components/admin/crm/ResultsView'
import SuppressionsView from '../../components/admin/crm/SuppressionsView'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'

const VIEWS = [
  { value: 'contacts', label: 'Contacts', icon: Users, Component: ContactsView },
  { value: 'compose', label: 'Compose', icon: PenLine, Component: ComposeView },
  { value: 'results', label: 'Results', icon: BarChart3, Component: ResultsView },
  { value: 'suppressions', label: 'Suppressions', icon: Ban, Component: SuppressionsView },
]

// Composing an HTML email and reading campaign results on a phone is a bad
// experience we'd rather not ship a half-measure of. The tab is desktop-only
// and says so plainly instead of degrading into something unusable.
function SmallScreenNotice() {
  return (
    <div className="lg:hidden">
      <div className="rounded-lg border border-border bg-muted/40 p-6 text-center">
        <Monitor className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <h2 className="text-base font-semibold">Open this on a desktop</h2>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
          The CRM needs the room — composing a campaign and reading its results
          doesn&rsquo;t fit on a phone. Everything else in the admin dashboard
          works here as usual.
        </p>
      </div>
    </div>
  )
}

export default function CrmManagement({ embedded = false }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const viewParam = searchParams.get('view')
  const [active, setActive] = useState(
    VIEWS.find((v) => v.value === viewParam)?.value || 'contacts',
  )

  // Campaigns are created in Compose and read in Results; carrying the id in
  // the URL means a results link can be shared and a reload doesn't lose it.
  const campaignId = searchParams.get('campaign')

  const onViewChange = (value) => {
    setActive(value)
    const params = new URLSearchParams(searchParams)
    if (value === 'contacts') params.delete('view')
    else params.set('view', value)
    setSearchParams(params, { replace: true })
  }

  const selectCampaign = (id, view = 'results') => {
    const params = new URLSearchParams(searchParams)
    if (id) params.set('campaign', id)
    else params.delete('campaign')
    if (view === 'contacts') params.delete('view')
    else params.set('view', view)
    setSearchParams(params, { replace: true })
    setActive(view)
  }

  const body = (
    <>
      <SmallScreenNotice />

      <div className="hidden lg:block">
        <Tabs value={active} onValueChange={onViewChange}>
          <TabsList className="mb-4">
            {VIEWS.map(({ value, label, icon: Icon }) => (
              <TabsTrigger key={value} value={value}>
                <span className="inline-flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  {label}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>

          {VIEWS.map(({ value, Component }) => (
            <TabsContent key={value} value={value}>
              {active === value && (
                <Component campaignId={campaignId} onSelectCampaign={selectCampaign} />
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </>
  )

  if (embedded) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">{body}</div>

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-4">CRM</h1>
        {body}
      </div>
    </Layout>
  )
}
